import { HttpClient, HttpContext } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { BypassErrorHandling, BypassErrorToast } from '@app/v2/shared/helpers/http-context-tokens'
import { AuthService } from '@auth/services/auth.service'
import { environment } from '@environments/environment'
import { CreateWorkspace } from '@workspaces/interfaces/create-workspace.interface'
import { BehaviorSubject, Observable, Subject } from 'rxjs'
import { map } from 'rxjs/operators'
import { ParentOrganization, Workspace } from '../interfaces/workspace.interface'

/**
 * Handles creating/ updating/ listing workspaces as well as assigning users
 */
@Injectable({
  providedIn: 'root'
})
export class WorkspaceService {
  /** Creates & inject service dependencies */
  constructor(private http: HttpClient, private auth: AuthService) {}

  /**
   * Determines if show no more workspaces should be shown or not
   */
  public stopRetrieving$: Subject<boolean> = new BehaviorSubject<boolean>(false)

  public spliceWorkspace$ = new Subject()
  /**
   * Creates a workspace
   * @param value CreateWorkspaace interface
   * @returns Observable
   */
  createWorkspace(value: CreateWorkspace): Observable<Workspace> {
    return this.http.post<Workspace>(`${environment.planningApi}workspaces`, value, {
      context: new HttpContext().set(BypassErrorToast, true).set(BypassErrorHandling, true)
    })
  }

  /**
   * Updates a workspace
   * @param workspace Workspace details
   * @returns Observable
   */
  updateWorkspace(workspace: any) {
    return this.http.patch(`${environment.planningApi}workspaces`, {
      ...workspace
    })
  }
  /**
   * gets a workspace and their sub entities
   * @param targetId workspace id
   * @returns workspaces list
   */
  getWorkspaces(targetId: number | null = null, page: number | null = 1, search: string | null = null): Observable<any> {
    const requestedTargetId = !targetId ? '' : `?workspace_id=${targetId}`
    const requestedPage = `${!targetId ? `?` : '&'}page=${page.toString()}`
    const searchParam = search ? `&search=${search}` : ''
    return this.http.get<any>(`${environment.planningApi}workspaces${requestedTargetId + requestedPage + searchParam}`)
  }

  /**
   * gets parent workspace
   * @param targetId workspace id
   * @returns workspaces list
   */
  getParentWorkspaces(targetId: number | null = null, page: number | null = 1, search: string | null = null): Observable<any> {
    const requestedTargetId = !targetId ? '' : `?workspace_id=${targetId}`
    const requestedPage = `${!targetId ? `?` : '&'}page=${page.toString()}`
    const searchParam = search ? `&search=${search}` : ''
    return this.http.get<any>(`${environment.planningApi}workspaces/main${requestedTargetId + requestedPage + searchParam}`)
  }

  /**
   * gets parent organizations
   * @returns organizations list
   */
  getParentOrganizations(): Observable<ParentOrganization[]> {
    return this.http
      .get<{ parent_organizations: ParentOrganization[] }>(`${environment.planningApi}organizations/get-parent-organizations`)
      .pipe(map((response) => response.parent_organizations))
  }
  /**
   * finds a single workspace
   * @param targetId workspace id
   * @returns workspace list
   */
  findWorkspaces(targetId: number, page: number | null = 1, search: string | null = null): Observable<any[]> {
    const requestedPage = `${!targetId ? `?` : '&'}page=${page.toString()}`
    const searchParam = search ? `&search=${search}` : ''
    return this.http.get<any[]>(`${environment.planningApi}workspaces/find?workspace_id=${targetId + requestedPage + searchParam}`)
  }
  /**
   * Assigns a user to a workspace
   * @param form user assign interface
   * @returns Observable
   */
  assignUser(form) {
    return this.http.post(`${environment.planningApi}workspaceUsers`, form)
  }

  /**
   * Returns a list of workspace users
   * @param id workspace id
   * @returns workspace users list
   */
  getWorkspaceUsers(id: number): Observable<any[]> {
    return this.http.get<any[]>(`${environment.planningApi}workspaceUsers?workspace_id=${id}`)
  }
  /**
   * Gets a list of current workspace users
   * @returns current workspace users
   */
  getCurrentWorkspaceUsers(): Observable<any[]> {
    return this.http.get<any[]>(`${environment.planningApi}workspaceUsers?workspace_id=${this.auth.currentWorkspace.id}`)
  }

  /**
   * Removes a user from a workspace
   * @param workspaceId workspace id
   * @param userId user id
   * @returns Observable
   */
  deleteUser(workspaceId: number, userId: string) {
    return this.http.post(`${environment.planningApi}workspaceUsers/delete`, {
      workspace_id: workspaceId,
      user_id: userId
    })
  }

  /**
   * Checks whether the current user can remove  workspace user
   * @param workspaceId workspaces id
   * @returns Observable
   */
  canDeleteUser(workspaceId): Observable<any> {
    return this.http.get(`${environment.planningApi}workspaceUsers/CanUnassign?workspace_id=${workspaceId}`)
  }

  /**
   * Update user role on the workspace
   * @param value inteface defining worksapce id, user id, role and email address
   * @returns Observable
   */
  updateRole(value: { user_id: number; workspace_id: number; is_owner: boolean; role: string; email?: string }) {
    return this.http.patch(`${environment.planningApi}workspaceUsers`, value)
  }

  /**
   * Gets available plans for a workspace
   * @returns Available plans
   */
  getPlans() {
    return this.http.get(`${environment.planningApi}plans`)
  }

  /**
   * Starts a workspace master export request
   * @returns Observable
   */
  export() {
    return this.http.post(
      `${environment.exportsApi}export/master/${this.auth.currentWorkspace.slug}/sendmail`,
      {},
      {
        headers: {
          Token: this.auth.getToken()
        }
      }
    )
  }

  /**
   * Checks the current progress of an active export request
   * @returns Observable
   */
  checkExportProgress() {
    return this.http.post(`${environment.exportsApi}export/master/${this.auth.currentWorkspace.slug}/progress`, {})
  }

  /**
   * Sets the logo for a workspace
   * @param url logo url
   * @param workspaceId workspace id
   * @returns Observable<Workspace>
   */
  updateLogo(url, workspaceId): Observable<Workspace> {
    return this.http.patch<Workspace>(`${environment.planningApi}workspaces`, {
      id: workspaceId,
      logo: url
    })
  }

  deleteWorkspace(workspaceId) {
    return this.http.patch(`${environment.planningApi}workspaces/delete`, {
      id: workspaceId
    })
  }

  getOrganizationTheme(workspaceId: number): Observable<{ theme: { enable: boolean; logo: string } }> {
    return this.http.get<{ theme: { enable: boolean; logo: string } }>(`${environment.v2.baseUrl}plan/get-organization-theme/${workspaceId}`, {
      context: new HttpContext().set(BypassErrorHandling, true).set(BypassErrorToast, true)
    })
  }
  /**
   * Checks if a slug is already in use
   * @param slug slug to check
   * @returns Observable
   */
  checkForDuplicateSlug(slug: string): Observable<{ slugTaken: boolean } | null> {
    return this.http
      .post<{ workspace_exist: boolean }>(`${environment.v2.baseUrl}plan/workspace_exist`, {
        slug
      })
      .pipe(
        map((response: any) => {
          return response.workspace_exist ? { slugTaken: true } : null
        })
      )
  }
}
